<template>
  <div class="state-tab" :class="{ expanded: isExpanded }">
    <!-- Two-column layout when expanded -->
    <div class="state-tab-layout">
      <!-- Diagram Column (only when expanded) -->
      <div v-if="isExpanded" class="diagram-column">
        <div class="diagram-header">
          <span class="diagram-title">State Flow</span>
        </div>
        <StateDiagram :current-state="aiState" :previous-state="previousState" />
      </div>

      <!-- Properties Column -->
      <div class="properties-column">
        <!-- AI Assistant State Machine (New Architecture) -->
        <div class="state-section ai-state-section">
          <div class="section-header">
            <span class="section-title">State Machine</span>
            <span class="state-badge" :class="aiState">{{ aiState }}</span>
          </div>
          <div class="state-tree">
            <div class="tree-item">
              <span class="key">state</span>
              <span class="value state-value" :class="aiState">{{ aiState }}</span>
            </div>
            <div class="tree-item">
              <span class="key">sessionId</span>
              <span class="value" :class="{ null: !machine.sessionId }">
                {{ machine.sessionId || "null" }}
              </span>
              <button
                v-if="machine.sessionId"
                class="copy-btn"
                @click="copyToClipboard(machine.sessionId, 'sessionId')"
                :title="copiedField === 'sessionId' ? 'Copied!' : 'Copy'"
              >
                <svg
                  v-if="copiedField !== 'sessionId'"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="12"
                  height="12"
                >
                  <rect x="9"
y="9" width="13" height="13"
rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="12"
                  height="12"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </button>
            </div>
            <div class="tree-item">
              <span class="key">recorderUrl</span>
              <a
                v-if="aiRecorderUrl"
                :href="aiRecorderUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="value url-value clickable-url"
                :title="aiRecorderUrl"
              >
                {{ truncateUrl(aiRecorderUrl) }}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="10"
                  height="10"
                  class="external-icon"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
              <span v-else class="value url-value null">null</span>
            </div>
            <div class="tree-item">
              <span class="key">locked</span>
              <span class="value boolean" :class="isLocked ? 'true' : 'false'">
                {{ isLocked }}
              </span>
            </div>
          </div>
        </div>

        <!-- Session Data -->
        <div class="state-section">
          <div class="section-header">
            <span class="section-title">Session Data</span>
            <span class="status-dot" :class="aiSessionData?.recorderSessionId ? 'active' : 'inactive'"></span>
          </div>
          <div class="state-tree">
            <div class="tree-item">
              <span class="key">recorderSessionId</span>
              <span class="value" :class="{ null: !aiSessionData?.recorderSessionId }">
                {{ aiSessionData?.recorderSessionId || "null" }}
              </span>
              <button
                v-if="aiSessionData?.recorderSessionId"
                class="copy-btn"
                @click="copyToClipboard(aiSessionData.recorderSessionId, 'recorderSessionId')"
                :title="copiedField === 'recorderSessionId' ? 'Copied!' : 'Copy'"
              >
                <svg
                  v-if="copiedField !== 'recorderSessionId'"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="12"
                  height="12"
                >
                  <rect x="9"
y="9" width="13" height="13"
rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="12"
                  height="12"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </button>
            </div>
            <div class="tree-item">
              <span class="key">licenseId</span>
              <span class="value">{{ aiSessionData?.licenseId || "null" }}</span>
            </div>
            <div class="tree-item">
              <span class="key">patientId</span>
              <span class="value">{{ aiSessionData?.patientId || "null" }}</span>
            </div>
            <div class="tree-item">
              <span class="key">hasSummaryData</span>
              <span class="value boolean" :class="!!aiSessionData?.summaryData ? 'true' : 'false'">
                {{ !!aiSessionData?.summaryData }}
              </span>
            </div>
          </div>
        </div>

        <!-- Templates -->
        <div class="state-section">
          <div class="section-header">
            <span class="section-title">Templates</span>
            <span class="status-dot" :class="machine.templates.length > 0 ? 'active' : 'inactive'"></span>
          </div>
          <div class="state-tree">
            <div class="tree-item">
              <span class="key">count</span>
              <span class="value number">{{ machine.templates.length }}</span>
            </div>
          </div>
        </div>

        <!-- Socket State -->
        <div class="state-section">
          <div class="section-header">
            <span class="section-title">Socket</span>
            <span class="status-dot" :class="socketConnected ? 'active' : 'inactive'"></span>
          </div>
          <div class="state-tree">
            <div class="tree-item">
              <span class="key">connected</span>
              <span class="value boolean" :class="socketConnected ? 'true' : 'false'">
                {{ socketConnected }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">reconnecting</span>
              <span class="value boolean" :class="isReconnecting ? 'true' : 'false'">
                {{ isReconnecting }}
              </span>
            </div>
          </div>
        </div>

        <!-- UI State -->
        <div class="state-section ui-state-section">
          <div class="section-header">
            <span class="section-title">UI State</span>
            <span class="status-dot" :class="uiPopoverVisible || uiRecorderVisible ? 'active' : 'inactive'"></span>
          </div>
          <div class="state-tree">
            <div class="tree-item">
              <span class="key">displayMode</span>
              <span class="value" :class="uiDisplayMode">
                {{ uiDisplayMode }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">isMinimized</span>
              <span class="value boolean" :class="uiIsMinimized ? 'true' : 'false'">
                {{ uiIsMinimized }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">showFab</span>
              <span class="value boolean" :class="uiShowFab ? 'true' : 'false'">
                {{ uiShowFab }}
              </span>
            </div>
            <div class="tree-item tree-separator">
              <span class="key separator-label">— Modals —</span>
            </div>
            <div class="tree-item">
              <span class="key">recorderVisible</span>
              <span class="value boolean" :class="uiRecorderVisible ? 'true' : 'false'">
                {{ uiRecorderVisible }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">processingModal</span>
              <span class="value boolean" :class="uiProcessingModalVisible ? 'true' : 'false'">
                {{ uiProcessingModalVisible }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">reviewModal</span>
              <span class="value boolean" :class="uiReviewModalVisible ? 'true' : 'false'">
                {{ uiReviewModalVisible }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">popoverVisible</span>
              <span class="value boolean" :class="uiPopoverVisible ? 'true' : 'false'">
                {{ uiPopoverVisible }}
              </span>
            </div>
          </div>
        </div>

        <!-- Recovery Mode State -->
        <div class="state-section">
          <div class="section-header">
            <span class="section-title">Recovery Mode</span>
            <span class="status-dot" :class="isInRecoveryMode ? 'warning' : 'inactive'"></span>
          </div>
          <div class="state-tree">
            <div class="tree-item">
              <span class="key">active</span>
              <span class="value boolean" :class="isInRecoveryMode ? 'true' : 'false'">
                {{ isInRecoveryMode }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">lastKnownStatus</span>
              <span class="value" :class="{ null: !lastKnownStatus }">
                {{ lastKnownStatus || "null" }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">showRefreshButton</span>
              <span class="value boolean" :class="showRefreshButton ? 'true' : 'false'">
                {{ showRefreshButton }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">pollingFailCount</span>
              <span class="value number">{{ pollingFailCount }}</span>
            </div>
          </div>
        </div>

        <!-- Session Lock State (from State Machine) -->
        <div class="state-section">
          <div class="section-header">
            <span class="section-title">Session Lock</span>
            <span class="status-dot" :class="aiLockedSessionId ? 'active' : 'inactive'"></span>
          </div>
          <div class="state-tree">
            <div class="tree-item">
              <span class="key">lockedSessionId</span>
              <span class="value" :class="{ null: !aiLockedSessionId }">
                {{ aiLockedSessionId || "null" }}
              </span>
              <button
                v-if="aiLockedSessionId"
                class="copy-btn"
                @click="copyToClipboard(aiLockedSessionId, 'lockedSessionId')"
                :title="copiedField === 'lockedSessionId' ? 'Copied!' : 'Copy'"
              >
                <svg
                  v-if="copiedField !== 'lockedSessionId'"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="12"
                  height="12"
                >
                  <rect x="9"
y="9" width="13" height="13"
rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="12"
                  height="12"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </button>
            </div>
            <div class="tree-item">
              <span class="key">isLocked</span>
              <span class="value boolean" :class="isLocked ? 'true' : 'false'">
                {{ isLocked }}
              </span>
            </div>
          </div>
        </div>

        <!-- Dev Settings State -->
        <div class="state-section">
          <div class="section-header">
            <span class="section-title">Dev Settings</span>
            <span class="status-dot" :class="hasActiveDevSettings ? 'warning' : 'inactive'"></span>
          </div>
          <div class="state-tree">
            <div class="tree-item">
              <span class="key">simulateSocketDisconnect</span>
              <span class="value boolean" :class="settings.simulateSocketDisconnect ? 'true' : 'false'">
                {{ settings.simulateSocketDisconnect }}
              </span>
            </div>
            <div class="tree-item">
              <span class="key">simulatePollingTimeout</span>
              <span class="value boolean" :class="settings.simulatePollingTimeout ? 'true' : 'false'">
                {{ settings.simulatePollingTimeout }}
              </span>
            </div>
          </div>
        </div>

        <!-- State History -->
        <div class="state-section history-section" :class="{ detached: isDetached }">
          <div
            class="section-header"
            :class="{ collapsible: !isDetached }"
            @click="!isDetached && (showHistory = !showHistory)"
          >
            <span class="section-title">State History</span>
            <div class="header-right">
              <span v-if="isDetached" class="detached-badge">Detached</span>
              <span v-else class="history-count">{{ stateHistory.length }}</span>

              <!-- Detach/Attach button -->
              <button
                class="detach-btn"
                @click.stop="toggleDetach"
                :title="isDetached ? 'Reattach to panel' : 'Detach to floating window'"
              >
                <svg
                  v-if="!isDetached"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="12"
                  height="12"
                >
                  <path d="M15 3h6v6" />
                  <path d="M10 14 21 3" />
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  width="12"
                  height="12"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>

              <svg
                v-if="!isDetached"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                width="12"
                height="12"
                class="chevron"
                :class="{ open: showHistory }"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </div>
          </div>

          <!-- When attached: show full history content -->
          <div v-if="!isDetached && showHistory" class="history-content">
            <div v-if="stateHistory.length > 0" class="history-actions">
              <button class="clear-btn" @click="clearHistory">Clear</button>
            </div>

            <div v-if="stateHistory.length === 0" class="history-empty">No state changes recorded yet</div>

            <div v-else class="history-list">
              <div v-for="entry in [...stateHistory].reverse()" :key="entry.id" class="history-entry">
                <div class="entry-header">
                  <span class="entry-source">{{ entry.source }}</span>
                  <span class="entry-key">.{{ entry.key }}</span>
                  <span class="entry-time">{{ formatHistoryTime(entry.timestamp) }}</span>
                </div>
                <div class="entry-change">
                  <span class="old-value">{{ formatValue(entry.oldValue) }}</span>
                  <span class="arrow">→</span>
                  <span class="new-value">{{ formatValue(entry.newValue) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import { useRecoveryMode } from "@/composables/useRecoveryMode";
import { useDevSettings } from "@/composables/useDevSettings";
import { useStateMachine, type AIAssistantState } from "@/composables/useStateMachine";
import { useDevPanel } from "@/composables/useDevPanel";
import { useStateHistory } from "@/composables/useStateHistory";
import StateDiagram from "../StateDiagram.vue";

// AI Assistant State Machine (singleton)
const machine = useStateMachine();

// Dev Panel state (for expanded mode)
const { isExpanded } = useDevPanel();

// Track previous state for diagram transitions
const previousState = ref<AIAssistantState | null>(null);

// Watch state changes to track previous state
watch(
  () => machine.state,
  (newState, oldState) => {
    if (oldState) {
      previousState.value = oldState as AIAssistantState;
    }
  }
);

// Computed values for template (simplified - no nested reviewStatus)
const aiState = computed(() => machine.state);
const aiSessionData = computed(() => machine.sessionData);
const aiRecorderUrl = computed(() => machine.recorderUrl);
const aiLockedSessionId = computed(() => machine.lockedSessionId);

const getStateStatusClass = computed(() => {
  switch (machine.state) {
    case "idle":
      return "inactive";
    case "form_template_selection":
    case "device_selection":
      return "active";
    case "recording":
      return "warning";
    case "processing":
      return "warning";
    case "review_ready":
      return "active";
    case "error":
      return "warning";
    default:
      return "inactive";
  }
});

const truncateUrl = (url?: string) => {
  if (!url) return null;
  if (url.length > 40) return url.slice(0, 40) + "...";
  return url;
};

// Copy to clipboard functionality
const copiedField = ref<string | null>(null);

const copyToClipboard = async (text: string, field: string) => {
  try {
    await navigator.clipboard.writeText(text);
    copiedField.value = field;
    setTimeout(() => {
      copiedField.value = null;
    }, 1500);
  } catch (err) {
    console.error("Failed to copy:", err);
  }
};

// Recovery Mode
const { isInRecoveryMode, lastKnownStatus, showRefreshButton, pollingFailCount } = useRecoveryMode();

// Session Lock (now from state machine)
const isLocked = computed(() => machine.lockedSessionId !== null);

// Dev Settings
const { settings } = useDevSettings();

// State History (shared composable)
const {
  stateHistory,
  isDetached,
  showHistory,
  addHistoryEntry,
  clearHistory,
  toggleDetach,
  formatHistoryTime,
  formatValue,
} = useStateHistory();

// Socket state (poll from window)
const socketConnected = ref(false);
const isReconnecting = ref(false);

// UI state - now using reactive refs from state machine directly
const uiDisplayMode = computed(() => machine.displayMode);
const uiIsMinimized = computed(() => machine.isUIMinimized);
const uiShowFab = computed(() => machine.showFab);
const uiRecorderVisible = computed(() => machine.isIn("recording") && machine.recorderVisible);
const uiPopoverVisible = computed(() => machine.isIn("review_ready", "processing"));
const uiProcessingModalVisible = computed(() => machine.isIn("processing") && machine.displayMode === "modal");
const uiReviewModalVisible = computed(() => machine.isIn("review_ready") && machine.displayMode === "modal");

const hasActiveDevSettings = computed(() => {
  return settings.value.simulateSocketDisconnect || settings.value.simulatePollingTimeout;
});

// Watch for State Machine changes (using refs for reactivity)
watch(machine.stateRef, (newVal, oldVal) => addHistoryEntry("StateMachine", "state", oldVal, newVal));

watch(
  () => machine.sessionDataRef.value?.recorderSessionId,
  (newVal, oldVal) => addHistoryEntry("StateMachine", "sessionId", oldVal, newVal)
);

watch(
  () => machine.recorderUrl,
  (newVal, oldVal) => addHistoryEntry("StateMachine", "recorderUrl", oldVal, newVal)
);

watch(
  () => machine.lockedSessionId,
  (newVal, oldVal) => addHistoryEntry("StateMachine", "lockedSessionId", oldVal, newVal)
);

watch(socketConnected, (newVal, oldVal) => addHistoryEntry("Socket", "connected", oldVal, newVal));

watch(isReconnecting, (newVal, oldVal) => addHistoryEntry("Socket", "reconnecting", oldVal, newVal));

watch(isInRecoveryMode, (newVal, oldVal) => addHistoryEntry("Recovery", "active", oldVal, newVal));

watch(lastKnownStatus, (newVal, oldVal) => addHistoryEntry("Recovery", "status", oldVal, newVal));

watch(showRefreshButton, (newVal, oldVal) => addHistoryEntry("Recovery", "showRefresh", oldVal, newVal));

// Session lock changes tracked via StateMachine watcher above

watch(
  () => settings.value.simulateSocketDisconnect,
  (newVal, oldVal) => addHistoryEntry("DevSettings", "socketDisconnect", oldVal, newVal)
);

watch(
  () => settings.value.simulatePollingTimeout,
  (newVal, oldVal) => addHistoryEntry("DevSettings", "pollingTimeout", oldVal, newVal)
);

// Watch UI state changes (using reactive refs from state machine)
watch(machine.displayModeRef, (newVal, oldVal) => addHistoryEntry("UI", "displayMode", oldVal, newVal));
watch(machine.minimizedRef, (newVal, oldVal) => addHistoryEntry("UI", "minimized", oldVal, newVal));
watch(machine.recorderVisibleRef, (newVal, oldVal) => addHistoryEntry("UI", "recorderVisible", oldVal, newVal));

// Poll socket state only (UI state now uses reactive refs from state machine)
let pollInterval: ReturnType<typeof setInterval> | null = null;

const pollSocketState = () => {
  // Try to get socket state from window (if exposed by useWebSocket)
  const win = window as unknown as {
    __aiAssistantSocketState?: { connected: boolean; reconnecting: boolean };
  };
  if (win.__aiAssistantSocketState) {
    socketConnected.value = win.__aiAssistantSocketState.connected || false;
    isReconnecting.value = win.__aiAssistantSocketState.reconnecting || false;
  }
};

onMounted(() => {
  pollSocketState();
  pollInterval = setInterval(pollSocketState, 1000); // Socket state polling
});

onUnmounted(() => {
  if (pollInterval) {
    clearInterval(pollInterval);
  }
});
</script>

<style scoped>
.state-tab {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* Critical for flex child scrolling */
  overflow: hidden;
}

/* Layout container */
.state-tab-layout {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 16px;
}

/* Diagram column (left side when expanded) */
.diagram-column {
  flex-shrink: 0;
  width: 380px;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  overflow: hidden;
}

.diagram-header {
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.diagram-title {
  font-size: 12px;
  font-weight: 600;
  color: #b0b0c0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Properties column (right side when expanded, full width when collapsed) */
.properties-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding-bottom: 8px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.properties-column::-webkit-scrollbar {
  width: 6px;
}

.properties-column::-webkit-scrollbar-track {
  background: transparent;
}

.properties-column::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.state-section {
  flex-shrink: 0; /* Prevent sections from shrinking - allows scroll */
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  overflow: hidden;
}

/* AI State Section highlight */
.ai-state-section {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.2);
}

/* UI State Section highlight */
.ui-state-section {
  background: rgba(168, 85, 247, 0.08);
  border-color: rgba(168, 85, 247, 0.2);
}

.value.widget {
  color: #9ece6a;
}

.value.modal {
  color: #f59e0b;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #b0b0c0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* State badge for AI state */
.state-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.state-badge.idle {
  background: rgba(128, 128, 128, 0.2);
  color: #9090a0;
}

.state-badge.form_template_selection,
.state-badge.device_selection {
  background: rgba(168, 85, 247, 0.2);
  color: #a78bfa;
}

.state-badge.recording {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
  animation: pulse 1.5s ease-in-out infinite;
}

.state-badge.processing {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  animation: pulse 1.5s ease-in-out infinite;
}

.state-badge.review_ready {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

.state-badge.error {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #404050;
}

.status-dot.active {
  background: #22c55e;
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.5);
}

.status-dot.warning {
  background: #f59e0b;
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.5);
  animation: pulse 1.5s ease-in-out infinite;
}

.status-dot.inactive {
  background: #606080;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.state-tree {
  padding: 10px 12px;
  font-family: "JetBrains Mono", "Fira Code", "SF Mono", Monaco, Consolas, monospace;
  font-size: 12px;
}

.tree-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.02);
}

.tree-item:last-child {
  border-bottom: none;
}

.tree-item.tree-separator {
  border-bottom: none;
  padding-top: 8px;
  padding-bottom: 4px;
}

.separator-label {
  color: #606080 !important;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.key {
  color: #bb9af7;
  min-width: 130px;
  flex-shrink: 0;
}

.value {
  color: #7dd3fc;
  word-break: break-all;
}

.value.null {
  color: #707090;
  font-style: italic;
}

.value.boolean.true {
  color: #9ece6a;
}

.value.boolean.false {
  color: #f7768e;
}

.value.number {
  color: #ff9e64;
}

.value.string {
  color: #9ece6a;
}

/* State value colors matching badge */
.value.state-value.idle {
  color: #9090a0;
}

.value.state-value.form_template_selection,
.value.state-value.device_selection {
  color: #a78bfa;
}

.value.state-value.recording {
  color: #f87171;
}

.value.state-value.processing {
  color: #fbbf24;
}

.value.state-value.review_ready {
  color: #4ade80;
}

.value.state-value.error {
  color: #f87171;
}

.value.url-value {
  font-size: 10px;
  color: #7dd3fc;
  opacity: 0.8;
}

/* Clickable URLs */
.clickable-url {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  color: #60a5fa !important;
  opacity: 1 !important;
  transition: color 0.15s ease;
}

.clickable-url:hover {
  color: #93c5fd !important;
  text-decoration: underline;
}

.external-icon {
  opacity: 0.6;
  flex-shrink: 0;
}

.clickable-url:hover .external-icon {
  opacity: 1;
}

/* Copy button */
.copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  margin-left: 6px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: #808098;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.copy-btn:hover {
  background: rgba(96, 165, 250, 0.15);
  border-color: rgba(96, 165, 250, 0.3);
  color: #60a5fa;
}

.copy-btn svg {
  width: 12px;
  height: 12px;
}

/* Collapsible Header */
.section-header.collapsible {
  cursor: pointer;
  user-select: none;
}

.section-header.collapsible:hover {
  background: rgba(255, 255, 255, 0.04);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.history-count {
  font-size: 10px;
  font-weight: 600;
  color: #707090;
  background: rgba(255, 255, 255, 0.06);
  padding: 2px 6px;
  border-radius: 8px;
}

.chevron {
  color: #707090;
  transition: transform 0.2s ease;
}

.chevron.open {
  transform: rotate(90deg);
}

/* History Section */
.history-section {
  background: rgba(0, 0, 0, 0.15);
}

.history-section.detached {
  background: rgba(96, 165, 250, 0.05);
  border-color: rgba(96, 165, 250, 0.15);
}

.history-section.detached .section-header {
  cursor: default;
}

.detached-badge {
  font-size: 9px;
  font-weight: 600;
  color: #60a5fa;
  background: rgba(96, 165, 250, 0.15);
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detach-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: #808098;
  cursor: pointer;
  transition: all 0.15s ease;
}

.detach-btn:hover {
  background: rgba(96, 165, 250, 0.15);
  border-color: rgba(96, 165, 250, 0.3);
  color: #60a5fa;
}

.history-content {
  padding-bottom: 6px;
}

.history-actions {
  display: flex;
  justify-content: flex-end;
  padding: 6px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.clear-btn {
  font-size: 10px;
  font-weight: 500;
  color: #808098;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 3px 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.clear-btn:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.history-empty {
  padding: 16px;
  text-align: center;
  font-size: 11px;
  color: #606080;
}

.history-list {
  display: flex;
  flex-direction: column;
}

.history-entry {
  padding: 8px 10px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.history-entry:last-child {
  border-bottom: none;
}

.entry-header {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 4px;
}

.entry-source {
  font-size: 10px;
  font-weight: 600;
  color: #60a5fa;
}

.entry-key {
  font-size: 10px;
  color: #bb9af7;
  font-family: "JetBrains Mono", "Fira Code", monospace;
}

.entry-time {
  font-size: 9px;
  color: #606080;
  margin-left: auto;
  font-family: "JetBrains Mono", "Fira Code", monospace;
}

.entry-change {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-family: "JetBrains Mono", "Fira Code", monospace;
}

.old-value {
  color: #f7768e;
  opacity: 0.8;
}

.arrow {
  color: #606080;
}

.new-value {
  color: #9ece6a;
}
</style>
